import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { createApi } from '@Pages/pageRuntime/sdk/api';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkApiFetcher,
  SdkApiHandle,
  SdkApiQueryKey,
  SdkUseApiOptions
} from '@Pages/pageRuntime/sdk/types';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { usePollCountdown, usePollRefetchInterval } from './pollInterval';

export function useApi<TData>(
  key: SdkApiQueryKey,
  fetcher: SdkApiFetcher<TData>,
  options?: SdkUseApiOptions<TData>
): SdkApiHandle<TData> {
  const queryClient = useQueryClient();
  const queryKey = [PAGE_API_QUERY_KEY_HEAD, ...key] as const;
  const poll = usePollRefetchInterval<TData, TData | undefined>(
    options?.pollInterval,
    (data) => data
  );
  const enabled = options?.enabled ?? true;

  const query = useQuery<TData, ReturnType<typeof toApiError>>({
    enabled,
    queryKey,
    refetchInterval: poll.refetchInterval,
    queryFn: async () => {
      try {
        return await fetcher(createApi(axios));
      } catch (error) {
        throw toApiError(error);
      }
    },
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined
  });
  const refreshIn = usePollCountdown(poll, query, enabled);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refreshIn,
    invalidate: () => queryClient.invalidateQueries({ queryKey, exact: true })
  };
}
